import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Button, ErrorResponse } from '~components';
import { TableView } from '~components/table-view';
import useEditableTableRows, { EditableRow } from '~support-at-home/hooks/useEditableTableRows';

import columnsFactory from '~support-at-home/forms/client-contributions/listSingle';

import { create, deleteOne, getAll, update } from '~support-at-home/actions/clientContributions';
import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectSahClientContributions } from '~support-at-home/selectors/clientContributions';
import { selectSahFundingTypes } from '~support-at-home/selectors/fundingTypes';

import { filterRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const ClientContributionsModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const clientContributions = useAppSelector(selectSahClientContributions);
  const fundingSources = useAppSelector(selectSahFundingTypes);
  const categories = useAppSelector(selectSahCategories);

  const columns = useMemo(() => columnsFactory(categories, fundingSources), [categories, fundingSources]);

  const persistedContributions: EditableRow[] = useMemo(
    () => clientContributions.map((contribution) => ({ ...contribution, persisted: true, backendId: contribution.id })),
    [clientContributions],
  );

  const getDefaultRow = useCallback(
    (): EditableRow => ({
      id: crypto.randomUUID(),
      start_date: '',
      end_date: '',
    }),
    [],
  );

  const load = useCallback(() => {
    dispatch(getAll({ client_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (isOpen && id !== null) {
      dispatch(getContributionCategories({ sort: ['sequence'] }));
      load();
    }
  }, [id, isOpen, load]);

  const { rows, tableRef, error, isProcessing, handleChange, canRowValidate, confirmChanges } = useEditableTableRows({
    source: persistedContributions,
    getDefaultRow,
    // Restrict the change fingerprint to persistable fields — the category_id column's
    // onChange snapshots a `category` object onto edited rows that the loaded data lacks,
    // which would otherwise register untouched values as phantom updates.
    diffKeys: ['category_id', 'funding_source_id', 'contrib_proportion', 'start_date', 'end_date'],
    onCreate: async (row) => {
      const toCreate = filterRowFields(row, columns, id);
      await dispatch(create(toCreate)).unwrap();
    },
    onUpdate: async (row) => {
      if (!row.backendId) return;
      const toUpdate = filterRowFields(row, columns, id);
      await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
    },
    onDelete: async (row) => {
      if (!row.backendId) return;
      await dispatch(deleteOne({ id: row.backendId })).unwrap();
    },
  });

  const handleConfirm = async () => {
    const outcome = await confirmChanges();
    if (outcome === 'saved') {
      load();
      onClose(true);
    } else if (outcome === 'clean') {
      onClose(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="xl" style={{ maxWidth: '1500px', width: '100%' }} data-cy="client-contributions-modal">
      <ModalHeader className="bg-success text-white">Allocated SAH Contributions</ModalHeader>
      <ModalBody>
        <ErrorResponse message={error} dataCy="client-contributions-modal-error" />
        <TableView
          ref={tableRef}
          id="client-contributions-modal"
          columns={columns}
          data={rows}
          editonly
          onChange={handleChange}
          canRowValidate={canRowValidate}
          canDeleteRow
          showAddRow
          emptyMessage="No data found"
          bordered
          compactView
        />
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <Button
          size="sm"
          color="success"
          className="ps-4 pe-4 mx-2"
          onClick={handleConfirm}
          loading={isProcessing}
          dataCy="client-contributions-modal-confirm-button"
        >
          Confirm
        </Button>
        <Button
          size="sm"
          className="ps-4 pe-4 mx-2"
          onClick={() => onClose(false)}
          disabled={isProcessing}
          dataCy="client-contributions-modal-cancel-button"
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientContributionsModal;
